import { useState, useEffect } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useAuth } from "../../../../firebase/AuthProvider";
import { createCIR, ManualCIRApprovalAsync } from "../../../../services/participantServices/ParticipantService";
import { toast } from "react-toastify";
import { GetCirListResDto, ParticipantUnderMeDto } from "../../../../interfaces/Participant";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

interface CIRFormData {
    incidentType: string;
    incidentDate: string;
    location: string;
    description: string;
    injuryOrHarm: boolean;
    witnesses: string[];
    actionsTaken: string;
    participantId: number
}

interface AddCIRModalProps {
    showAddCirModal: boolean;
    hanleCloseShowCIRModal: () => void;
    participantList: ParticipantUnderMeDto[];
    refreshList : () => void;
    viewCIR?: GetCirListResDto;
}

const AddCIRModal: React.FC<AddCIRModalProps> = ({
    showAddCirModal,
    hanleCloseShowCIRModal,
    participantList,
    viewCIR,
    refreshList
}) => {
    //   const [show, setShow] = useState(false);
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<CIRFormData>({
        incidentType: "",
        incidentDate: "",
        location: "",
        description: "",
        injuryOrHarm: false,
        witnesses: [""],
        actionsTaken: "",
        participantId: 0
    });

    // State for reason modal
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState("");
    const [reasonType, setReasonType] = useState<"approve" | "reject">("approve");
    const [currentApprovalRequestId, setCurrentApprovalRequestId] = useState<string | null>(null);

    // Set form data from viewCIR if present
    useEffect(() => {
        if (viewCIR) {
            setFormData({
                incidentType: viewCIR.incidentType,
                incidentDate: viewCIR.incidentDate.slice(0, 10), // YYYY-MM-DD
                location: viewCIR.location,
                description: viewCIR.description,
                injuryOrHarm: false, // Set if you have this field in your DTO
                witnesses: Array.isArray(viewCIR.witnesses)
                    ? viewCIR.witnesses
                    : JSON.parse(viewCIR.witnesses || "[]"),
                actionsTaken: viewCIR.actionsTaken || "",
                participantId: viewCIR.participantId
            });
        }
    }, [viewCIR]);

    const isViewMode = !!viewCIR;

    // Helper function to render approve/reject buttons
    const renderApprovalButtons = (cir: GetCirListResDto) => {
        // Show buttons based on current status
        return (
            <>
                {/* Show Approve button only if status is not already APPROVED */}
                {cir.status !== "APPROVED" && (
                    <Button 
                        variant="success" 
                        className="me-2 d-flex align-items-center" 
                        onClick={() => {
                            // Always allow approval, even without approvalRequestId
                            console.log('Setting up approve modal');
                            setReasonType("approve");
                            setCurrentApprovalRequestId(cir.id);
                            setReasonText("");
                            
                            // Use setTimeout to ensure state is updated before showing modal
                            setTimeout(() => {
                                setShowReasonModal(true);
                            }, 0);
                        }}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Approve
                    </Button>
                )}
                
                {/* Show Reject button only if status is not already REJECTED */}
                {cir.status !== "REJECTED" && (
                    <Button 
                        variant="danger" 
                        className="me-2 d-flex align-items-center" 
                        onClick={() => {
                            // Always allow rejection, even without approvalRequestId
                            console.log('Setting up reject modal');
                            setReasonType("reject");
                            setCurrentApprovalRequestId(cir.id);
                            setReasonText("");
                            
                            // Use setTimeout to ensure state is updated before showing modal
                            setTimeout(() => {
                                setShowReasonModal(true);
                            }, 0);
                        }}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                        Reject
                    </Button>
                )}
            </>
        );
    };

    //   const handleClose = () => setShow(false);
    //   const handleShow = () => setShow(true);

    // ✅ Handle text, textarea, select, and checkbox inputs
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;

        setFormData({
            ...formData,
            [name]: type === "checkbox"
                ? (e.target as HTMLInputElement).checked // ✅ Type assertion for checkboxes
                : value,
        });
    };


    // ✅ Handle witness array input change
    const handleWitnessChange = (index: number, value: string) => {
        const updatedWitnesses = [...formData.witnesses];
        updatedWitnesses[index] = value;
        setFormData({ ...formData, witnesses: updatedWitnesses });
    };

    // ✅ Add a new witness field
    const addWitness = () => {
        setFormData({ ...formData, witnesses: [...formData.witnesses, ""] });
    };

    // ✅ Remove a witness field
    const removeWitness = (index: number) => {
        const updatedWitnesses = formData.witnesses.filter((_, i) => i !== index);
        setFormData({ ...formData, witnesses: updatedWitnesses });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (!currentUser) return;

            const token = await currentUser.getIdToken();

            // ✅ Combine selected date with current time, outputting ISO format
            const selectedDate = new Date(formData.incidentDate);
            const now = new Date();

            // Merge current time into selected date
            selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

            // ✅ Convert to ISO string for .NET DateTime parsing
            const formattedIncidentDate = selectedDate.toISOString();

            const cirData = {
                participantId: formData.participantId,
                incidentType: formData.incidentType,
                incidentDate: formattedIncidentDate, // ✅ Send ISO string
                location: formData.location,
                description: formData.description,
                injuryOrHarm: formData.injuryOrHarm,
                witnesses: formData.witnesses,
                actionsTaken: formData.actionsTaken,
                attachments: [], 
            };

            const response = await createCIR(cirData, token);

            if (response.success) {
                toast.success("CIR Created Successfully");
                hanleCloseShowCIRModal()
                refreshList();
                //   handleClose();
                setFormData({
                    incidentType: "",
                    incidentDate: "",
                    location: "",
                    description: "",
                    injuryOrHarm: false,
                    witnesses: [""],
                    actionsTaken: "",
                    participantId: 0
                });
            } else {
                 toast.error(`Failed to create CIR: ${response.message}`);
            }
        } catch (error) {
            console.error("Error creating CIR:", error);
            toast.error("Something went wrong!");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* CIR Modal */}
            <Modal show={showAddCirModal} onHide={hanleCloseShowCIRModal} centered className="custom_popup" size="lg" backdrop={loading ? "static" : true} keyboard={!loading}>
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>
                        {isViewMode ? "View Critical Incident" : "Add Critical Incident"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        {/* Incident Type */}
                        <Form.Group className="custom_input_flled">
                            <Form.Label>Incident Type</Form.Label>
                            <Form.Control
                             className="ml-4"
                                type="text"
                                name="incidentType"
                                placeholder="Enter incident type"
                                value={formData.incidentType}
                                onChange={handleChange}
                                required
                                disabled={isViewMode}
                            />
                        </Form.Group>

                        {/* Incident Date */}
                        <Row>
                            <Col md={12}>
                                <Form.Group className="custom_input_flled">
                                    <Form.Label>Date of Incident</Form.Label>
                                    <Form.Control
                                     className="ml-4 px-5"
                                        type="date"
                                        name="incidentDate"
                                        value={formData.incidentDate}
                                        onChange={handleChange}
                                        required
                                        disabled={isViewMode}
                                        max={new Date().toISOString().split("T")[0]}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group
                                    className="custom_input_flled mb-3"
                                    controlId="participantSelect"
                                >
                                    <Form.Label>Select Participants</Form.Label>
                                    <Form.Select
                                     className="ml-4"
                                        aria-label="Select participant"
                                        name="participantId"
                                        value={formData.participantId || ""}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                participantId: Number(e.target.value),
                                            })
                                        }
                                        required
                                        disabled={isViewMode}
                                    >
                                        <option value="">Select participant</option>
                                        {participantList.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.firstName} {p.lastName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                        </Row>


                        {/* Location */}
                        <Form.Group className="custom_input_flled">
                            <Form.Label>Location</Form.Label>
                            <Form.Control
                                type="text"
                                name="location"
                                placeholder="Enter location"
                                value={formData.location}
                                onChange={handleChange}
                                required
                                disabled={isViewMode}
                                className="ml-4"
                            />
                        </Form.Group>

                        {/* Description */}
                        <Form.Group className="custom_input_flled">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                             className="ml-4"
                                as="textarea"
                                rows={3}
                                name="description"
                                placeholder="Enter description"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                disabled={isViewMode}
                            />
                        </Form.Group>

                        {/* Injury or Harm */}
                        <Form.Group className="mb-3">
                            <Form.Check
                             className="ml-4 dark-check-border mt-2"
                                type="checkbox"
                                name="injuryOrHarm"
                                label="Was there an injury or harm?"
                                checked={formData.injuryOrHarm}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        {/* Witnesses */}
                        <Form.Group className="custom_input_flled">
                            <Form.Label className="mb-5">Witnesses</Form.Label>
                            <div className="d-flex flex-column">
                                {formData.witnesses.map((witness, index) => (
                                    <div key={index} className="d-flex mb-2">
                                        <Form.Control
                                        className="ml-4 mt-2"
                                            type="text"
                                            placeholder="Enter witness name"
                                            value={witness}
                                            onChange={(e) => handleWitnessChange(index, e.target.value)}
                                            disabled={isViewMode}
                                        />
                                        {!isViewMode && formData.witnesses.length > 1 && (
                                            <Button
                                                variant="danger"
                                                className="ms-2"
                                                onClick={() => removeWitness(index)}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                ))}
                                
                                {!isViewMode && (
                                    <Button variant="primary" className="btn_sm ml-4" onClick={addWitness}>
                                        Add More Witnesses
                                    </Button>
                                )}
                            </div>
                        </Form.Group>

                        {/* Actions Taken */}
                        <Form.Group className="custom_input_flled">
                            <Form.Label>Actions Taken</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="actionsTaken"
                                placeholder="Enter actions taken"
                                value={formData.actionsTaken}
                                onChange={handleChange}
                                disabled={isViewMode}
                            />
                        </Form.Group>

                        {/* Buttons */}
                        <div className="d-flex justify-content-end mb-4">
                            {/* APPROVE/REJECT buttons */}
                            {viewCIR && renderApprovalButtons(viewCIR)}
                            <Button variant="secondary" className="me-2" onClick={hanleCloseShowCIRModal} disabled={loading}>
                                Close
                            </Button>
                            {!isViewMode && (
                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? "Submitting..." : "Submit"}
                                </Button>
                            )}
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Custom Reason Modal */}
            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {reasonType === "approve" ? "Approval Reason" : "Rejection Reason"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>
                            Please enter {reasonType === "approve" ? "approval" : "rejection"} reason:
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder={`Type your ${reasonType === "approve" ? "approval" : "rejection"} reason here...`}
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReasonModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant={reasonType === "approve" ? "success" : "danger"}
                        onClick={async () => {
                            if (!reasonText.trim()) {
                                toast.warning("Please enter a reason");
                                return;
                            }
                            
                            if (!currentUser) {
                                toast.error("User not authenticated");
                                return;
                            }

                            setLoading(true);
                            
                            try {
                                const token = await currentUser.getIdToken();
                                const cirId = viewCIR?.taskId?.toString()  || viewCIR?.approvalRequestId || currentApprovalRequestId;
                                
                                console.log('Debug - viewCIR:', viewCIR);
                                console.log('Debug - viewCIR keys:', viewCIR ? Object.keys(viewCIR) : 'viewCIR is null/undefined');
                                console.log('Debug - currentApprovalRequestId:', currentApprovalRequestId);
                                console.log('Debug - cirId:', cirId);
                                
                                if (!cirId) {
                                    toast.error("CIR ID not found. Cannot process approval.");
                                    setLoading(false);
                                    return;
                                }
                                
                                const status = reasonType === "approve" ? 1 : 0; // 1 for approval, 0 for rejection

                                const response = await ManualCIRApprovalAsync(token, cirId, status, reasonText);
                                
                                if (response.success) {
                                    const action = reasonType === "approve" ? "Approved" : "Rejected";
                                    toast.success(`CIR ${action} Successfully`);
                                    refreshList();
                                    hanleCloseShowCIRModal();
                                } else {
                                    const action = reasonType === "approve" ? "approve" : "reject";
                                    toast.error(`Failed to ${action} CIR: ${response.message}`);
                                }
                                
                                setShowReasonModal(false);
                                setReasonText("");
                            } catch (error) {
                                console.error("Error processing approval/rejection:", error);
                                toast.error("Something went wrong!");
                            } finally {
                                setLoading(false);
                            }
                        }}
                        disabled={loading}
                    >
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AddCIRModal;
